import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import { Contract, ContractCreate, PricingHeader, Customer, Invoice } from '../types';
import Modal from '../components/Modal';
import ContractForm from '../components/ContractForm';
import FilterToggle from '../components/FilterToggle';
import BulkOperations from '../components/BulkOperations';
import BulkInvoiceCreationModal from '../components/BulkInvoiceCreationModal';
import SearchInput from '../components/SearchInput';

const Contracts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [pricingHeaders, setPricingHeaders] = useState<PricingHeader[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [preselectedCustomerId, setPreselectedCustomerId] = useState<number | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'deleted'>('active');
  const [hideInactiveExpired, setHideInactiveExpired] = useState(false);
  const [hideWithoutCustomer, setHideWithoutCustomer] = useState(true);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSyncingUpcoming, setIsSyncingUpcoming] = useState(false);
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState(false);
  const [bulkInvoiceFeedback, setBulkInvoiceFeedback] = useState<{
    type: 'success' | 'warning' | 'error';
    text: string;
  } | null>(null);

  // Initialize state from URL on mount or when the URL search string changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page') || '1', 10);
    const statusParam = (params.get('status') as 'all' | 'active' | 'deleted') || undefined;
    const custParam = params.get('customer_id');
    const searchParam = params.get('search');
    const hideInactiveParam = params.get('hide_inactive_expired');
    const hideWithoutCustomerParam = params.get('hide_without_customer');
    const orderByParam = params.get('order_by');
    const orderDirParam = params.get('order_dir');

    if (!Number.isNaN(pageParam) && pageParam > 0) setCurrentPage(pageParam);
    if (statusParam) setFilterStatus(statusParam);
    if (custParam) setSelectedCustomerId(Number(custParam));
    if (searchParam !== null) setSearchTerm(searchParam);
    if (hideInactiveParam !== null) setHideInactiveExpired(hideInactiveParam === 'true');
    if (hideWithoutCustomerParam !== null) setHideWithoutCustomer(hideWithoutCustomerParam === 'true');
    if (orderByParam) setOrderBy(orderByParam);
    if (orderDirParam === 'asc' || orderDirParam === 'desc') setOrderDir(orderDirParam);
  }, [location.search]);
  // Bulk operations state
  const [selectedContracts, setSelectedContracts] = useState<string[]>([]);
  const [showBulkOps, setShowBulkOps] = useState(false);
  // Invoice dropdown state
  const [expandedContracts, setExpandedContracts] = useState<Set<string>>(new Set());
  const [contractInvoices, setContractInvoices] = useState<Record<string, Invoice[]>>({});
  const [loadingInvoices, setLoadingInvoices] = useState<Record<string, boolean>>({});
  const [boletoStatuses, setBoletoStatuses] = useState<Record<string, any>>({});
  const [invoiceCounts, setInvoiceCounts] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState<number>(10);
  // Sorting state
  const [orderBy, setOrderBy] = useState<string>('contract_number');
  const [orderDir, setOrderDir] = useState<'asc' | 'desc'>('asc');
  const handleSort = (key: string) => {
    if (orderBy === key) {
      setOrderDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrderDir('asc');
    }
    setCurrentPage(1);
  };
  const renderSortIcon = (key: string) => (orderBy === key ? (orderDir === 'asc' ? '↑' : '↓') : '↕️');

  // Central resolver for invoice count used for rendering and sorting
  const resolveInvoiceCount = (c: Contract): number => {
    const server = (c as any).invoice_count;
    if (typeof server === 'number') return server;
    const cached = invoiceCounts[c.contract_id];
    if (typeof cached === 'number') return cached;
    const loaded = contractInvoices[c.contract_id]?.length;
    return typeof loaded === 'number' ? loaded : 0;
  };

  
  const getCustomerName = (id: number) => {
    const customer = customers.find((c: Customer) => c.id === id);
    return customer?.customer_name || `Customer ${id}`;
  };

  
  // Get contract status with more detail
  const getContractStatus = (contract: Contract) => {
    if (!contract.isactive || contract.isdeleted) {
      return { label: 'Deleted', className: 'badge-danger' };
    }

    const currentDate = new Date();
    
    // Check if inactive (start_date equals end_date)
    if (contract.start_date && contract.end_date) {
      const startDate = new Date(contract.start_date).toDateString();
      const endDate = new Date(contract.end_date).toDateString();
      if (startDate === endDate) {
        return { label: 'Inactive', className: 'badge-secondary' };
      }
    }

    // Check if expired
    if (contract.end_date) {
      const endDate = new Date(contract.end_date);
      if (endDate < currentDate) {
        return { label: 'Expired', className: 'badge-warning' };
      }
    }

    return { label: 'Active', className: 'badge-success' };
  };

  // Fetch contracts with all filters applied on the server side
  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Map filterStatus to API params
      const statusParam = filterStatus; // 'all' | 'active' | 'deleted'
      const isActive = filterStatus === 'active' ? true : undefined;
      
      // Fetch contracts with server-side filtering
      const contractsRes = await apiService.getContracts(
        currentPage,
        pageSize,
        isActive,
        selectedCustomerId || undefined,
        searchTerm || undefined,
        hideInactiveExpired || undefined,
        hideWithoutCustomer || undefined,
        orderBy,
        orderDir,
        statusParam
      );
      
      setContracts(contractsRes.items || []);
      setTotalPages(contractsRes.totalPages || 1);
      // Preload invoice counts for visible contracts
      const ids = (contractsRes.items || []).map((c: Contract) => c.contract_id);
      if (ids.length > 0) {
        try {
          const batch = await apiService.getInvoicesByContractsBatch(ids);
          const counts: Record<string, number> = {};
          Object.entries(batch || {}).forEach(([cid, invs]) => {
            counts[cid] = Array.isArray(invs) ? invs.length : 0;
          });
          setInvoiceCounts(counts);
        } catch (e) {
          console.warn('Failed to batch load invoice counts', e);
          setInvoiceCounts({});
        }
      } else {
        setInvoiceCounts({});
      }
      
      console.log('Fetched contracts with filters:', {
        page: currentPage,
        contracts: contractsRes.items?.length || 0,
        totalPages: contractsRes.totalPages || 1,
        filters: {
          filterStatus,
          isActive,
          selectedCustomerId,
          searchTerm,
          hideInactiveExpired,
          hideWithoutCustomer,
          orderBy,
          orderDir,
          statusParam
        }
      });
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filterStatus, selectedCustomerId, searchTerm, hideInactiveExpired, hideWithoutCustomer, orderBy, orderDir]);

  const refreshContractsAfterBulk = useCallback(async (contractId?: string) => {
    try {
      const result = await apiService.contracts.syncBillingPeriods(contractId);
      await fetchContracts();
      
      // Show success feedback
      if (contractId) {
        setBulkInvoiceFeedback({
          type: 'success',
          text: `Successfully synced billing periods for contract #${contractId}`
        });
      } else {
        const successCount = result?.successful_contracts || result?.total_contracts || 0;
        const linesUpdated = result?.total_lines_updated || 0;
        setBulkInvoiceFeedback({
          type: 'success',
          text: `Successfully synced billing periods for ${successCount} contracts (${linesUpdated} lines updated)`
        });
      }
      
      // Auto-dismiss after 5 seconds
      setTimeout(() => setBulkInvoiceFeedback(null), 5000);
    } catch (error) {
      console.error('Failed to refresh contracts after bulk invoice creation:', error);
      setBulkInvoiceFeedback({
        type: 'error',
        text: `Failed to sync billing periods: ${error instanceof Error ? error.message : String(error)}`
      });
      // Auto-dismiss after 8 seconds
      setTimeout(() => setBulkInvoiceFeedback(null), 8000);
    }
  }, [fetchContracts]);

  // Fetch supporting data (customers, pricing headers, etc.)
  const fetchSupportingData = async () => {
    try {
      const [pricingHeadersRes, customersRes] = await Promise.all([
        apiService.getPricingHeaders(0, 100), // Get all for dropdown
        apiService.getCustomers(1, 1000), // Get more customers for dropdown
      ]);
      
      // Ensure we have arrays for the dropdowns
      setPricingHeaders(Array.isArray(pricingHeadersRes) ? pricingHeadersRes : []);
      setCustomers(Array.isArray(customersRes.items) ? customersRes.items : []);
      
      console.log('Fetched supporting data:', {
        pricingHeaders: Array.isArray(pricingHeadersRes) ? pricingHeadersRes.length : 0,
        customers: Array.isArray(customersRes.items) ? customersRes.items.length : 0
      });
    } catch (error) {
      console.error('Error fetching supporting data:', error);
      setPricingHeaders([]);
      setCustomers([]);
    }
  };

  // Initial load - fetch supporting data once
  useEffect(() => {
    fetchSupportingData();
  }, []);

  // Keep URL in sync with filters so refresh preserves state
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage && currentPage !== 1) params.set('page', String(currentPage));
    if (filterStatus) params.set('status', filterStatus);
    if (selectedCustomerId) params.set('customer_id', String(selectedCustomerId));
    if (searchTerm) params.set('search', searchTerm);
    if (hideInactiveExpired) params.set('hide_inactive_expired', String(hideInactiveExpired));
    if (hideWithoutCustomer) params.set('hide_without_customer', String(hideWithoutCustomer));
    if (orderBy) {
      params.set('order_by', orderBy);
      params.set('order_dir', orderDir);
    }
    navigate({ pathname: '/contracts', search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  }, [currentPage, filterStatus, selectedCustomerId, searchTerm, hideInactiveExpired, hideWithoutCustomer, orderBy, orderDir, navigate]);

  // Fetch contracts whenever filters or page changes
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Auto-open create modal when navigated from CustomerDetails with a customer in navigation state
  useEffect(() => {
    const state = location.state as any;
    const incomingCustomerId: number | undefined = state?.customerId || state?.createForCustomer?.id;
    if (incomingCustomerId) {
      setEditingContract(null);
      setPreselectedCustomerId(incomingCustomerId);
      setShowModal(true);
      // Clear state to prevent reopening on back/refresh
      navigate({ pathname: '/contracts', search: location.search }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Reset to page 1 when filters change (except page itself)
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, searchTerm, hideInactiveExpired, hideWithoutCustomer, selectedCustomerId, pageSize]);

  const handleCreate = () => {
    setEditingContract(null);
    setShowModal(true);
  };

  const handleEdit = (contract: Contract) => {
    setEditingContract(contract);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      try {
        await apiService.deleteContract(id);
        fetchContracts();
      } catch (error) {
        console.error('Error deleting contract:', error);
      }
    }
  };

  const handleRestore = async (id: string) => {
    try {
      await apiService.axiosInstance.put(`/api/v1/contracts/${id}`, { 
        isactive: true, 
        isdeleted: false 
      });
      fetchContracts();
    } catch (error) {
      console.error('Error restoring contract:', error);
    }
  };

  const handleSubmit = async (data: ContractCreate) => {
    try {
      if (editingContract) {
        await apiService.updateContract(editingContract.contract_id, data);
      } else {
        const result = await apiService.createContract(data);
        
        // Check if this was a supervisor contract creation
        if ('contract' in result && 'contract_lines' in result) {
          console.log('Supervisor contract created successfully:', {
            contractId: result.contract.contract_id,
            contractName: result.contract.contract_name,
            linesCreated: result.contract_lines.length,
            errors: result.errors
          });
          
          // If there were errors creating contract lines, show them
          if (result.errors && result.errors.length > 0) {
            console.warn('Some contract lines could not be created:', result.errors);
            // You could show a notification here if you have a notification system
          }
        } else {
          console.log('Regular contract created successfully:', result);
        }
      }
      setShowModal(false);
      fetchContracts();
    } catch (error) {
      console.error('Error saving contract:', error);
      // You could show an error notification here
    }
  };

  const handleCreateInvoice = (contract: Contract) => {
    // Navigate to invoices page with contract data
    navigate('/invoices', { state: { contractId: contract.contract_id, contractName: contract.contract_name } });
  };

  // Fetch boleto status for invoices with boleto payment method
  const fetchBoletoStatusesForInvoices = async (invoices: Invoice[]) => {
    const statuses: Record<string, any> = {};
    
    // Only fetch for invoices with boleto payment method
    const boletoInvoiceIds = invoices
      .filter(inv => inv.payment_method === 'boleto' && inv.currency === 'BRL')
      .map(inv => inv.invoice_id);
    
    for (const invoiceId of boletoInvoiceIds) {
      try {
        const response = await apiService.getBoletoStatus(invoiceId);
        statuses[invoiceId] = response;
      } catch (error) {
        console.error(`Error fetching boleto status for invoice ${invoiceId}:`, error);
        statuses[invoiceId] = null;
      }
    }
    
    return statuses;
  };

  // Toggle invoice dropdown for a contract
  const toggleInvoiceDropdown = async (contractId: string) => {
    const newExpanded = new Set(expandedContracts);
    
    if (newExpanded.has(contractId)) {
      newExpanded.delete(contractId);
    } else {
      newExpanded.add(contractId);
      
      // Fetch invoices for this contract if not already loaded
      if (!contractInvoices[contractId]) {
        setLoadingInvoices(prev => ({ ...prev, [contractId]: true }));
        try {
          const invoices = await apiService.getInvoicesByContract(contractId);
          const finalizedInvoices = invoices.filter((inv: Invoice) => inv.invoice_status === 'finalized');
          
          // Fetch boleto statuses for finalized invoices only
          const boletoStatusesForContract = await fetchBoletoStatusesForInvoices(finalizedInvoices);
          setBoletoStatuses(prev => ({ ...prev, ...boletoStatusesForContract }));
          
          setContractInvoices(prev => ({ ...prev, [contractId]: invoices }));
        } catch (error) {
          console.error(`Error fetching invoices for contract ${contractId}:`, error);
          setContractInvoices(prev => ({ ...prev, [contractId]: [] }));
        } finally {
          setLoadingInvoices(prev => ({ ...prev, [contractId]: false }));
        }
      }
    }
    
    setExpandedContracts(newExpanded);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDateUTC = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', { timeZone: 'UTC' });

  // Get payment status badge class
  const getPaymentStatusBadgeClass = (status: string) => {
    const statusClasses: Record<string, string> = {
      not_due: 'badge-light',
      pending: 'badge-warning',
      paid: 'badge-success',
      partial: 'badge-info',
      overdue: 'badge-danger',
      cancelled: 'badge-secondary',
      refunded: 'badge-primary',
      disputed: 'badge-warning',
      written_off: 'badge-dark',
      // Boleto specific statuses
      processing: 'badge-info',
      completed: 'badge-success',
      expired: 'badge-secondary',
      failed: 'badge-danger'
    };
    return statusClasses[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <h1>Contracts</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create Contract
        </button>
      </div>

      {/* Action Bar with Bulk Operations */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4 flex gap-2">
        <button
          onClick={() => setShowBulkOps(!showBulkOps)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          disabled={selectedContracts.length === 0}
        >
          Bulk Operations ({selectedContracts.length})
        </button>
        <button
          onClick={() => setSelectedContracts([])}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
        >
          Clear Selection
        </button>
        <button
          onClick={() => {
            setBulkInvoiceFeedback(null);
            setShowBulkInvoiceModal(true);
          }}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          disabled={selectedContracts.length === 0}
          title="Bulk create invoices based on selected contracts"
        >
          Bulk Create Invoices
        </button>
        <button
          onClick={async () => {
            setIsSyncingUpcoming(true);
            try {
              // If contracts are selected, sync only those; otherwise sync all
              const contractId = selectedContracts.length === 1 ? selectedContracts[0] : undefined;
              await refreshContractsAfterBulk(contractId);
            } finally {
              setIsSyncingUpcoming(false);
            }
          }}
          className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          disabled={isSyncingUpcoming}
          title={selectedContracts.length === 1 
            ? "Recalculate billing periods for selected contract (uses only finalized invoices)" 
            : selectedContracts.length > 1
              ? "Recalculate billing periods for all contracts (uses only finalized invoices)"
              : "Recalculate upcoming start for all contracts (uses only finalized invoices, ignores draft/voided/deleted)"
          }
        >
          {isSyncingUpcoming ? 'Recalculating…' : selectedContracts.length === 1 
            ? 'Recalculate Selected Contract' 
            : 'Recalculate Upcoming Dates'
          }
        </button>
      </div>

      {bulkInvoiceFeedback && (
        <div className={`alert alert-${bulkInvoiceFeedback.type === 'error' ? 'danger' : bulkInvoiceFeedback.type} mb-4`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
          <span>{bulkInvoiceFeedback.text}</span>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setBulkInvoiceFeedback(null)}
            aria-label="Dismiss bulk invoice feedback"
          >
            ×
          </button>
        </div>
      )}

      {/* Bulk Operations Panel */}
      {showBulkOps && (
        <div className="mb-4">
          <BulkOperations
            entityType="contracts"
            selectedIds={selectedContracts}
            onOperationComplete={() => {
              setSelectedContracts([]);
              fetchContracts();
            }}
            onClearSelection={() => setSelectedContracts([])}
          />
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <SearchInput
              onSearch={setSearchTerm}
              placeholder="Search contracts by name, number, or customer..."
              className="flex-1"
              initialValue={searchTerm}
            />

            {/* Customer Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Customer:</label>
              <select
                value={selectedCustomerId || ''}
                onChange={(e) => setSelectedCustomerId(e.target.value ? Number(e.target.value) : null)}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '200px'
                }}
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.customer_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Status:</label>
              <FilterToggle
                value={filterStatus}
                onChange={setFilterStatus}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'active', label: 'Active' },
                  { value: 'deleted', label: 'Deleted' }
                ]}
              />
            </div>

            {/* Additional Filters */}
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hideInactiveExpired}
                  onChange={(e) => setHideInactiveExpired(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Hide Inactive/Expired</span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hideWithoutCustomer}
                  onChange={(e) => setHideWithoutCustomer(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ fontSize: '14px' }}>Hide Without Customer</span>
              </label>
            </div>

            {/* Page Size */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: 500, whiteSpace: 'nowrap' }} title="Rows per page">Rows:</label>
              <select
                aria-label="Rows per page"
                value={pageSize}
                onChange={(e) => {
                  const value = e.target.value;
                  setPageSize(value === 'all' ? 1000 : Number(value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '6px 8px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  minWidth: '72px'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
            </div>

            {/* Results info */}
            <div style={{ marginLeft: 'auto', color: '#666', fontSize: '14px' }}>
              Showing {contracts.length} contracts
              {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
            </div>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedContracts.length === contracts.length && contracts.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedContracts(contracts.map((c: Contract) => c.contract_id));
                      } else {
                        setSelectedContracts([]);
                      }
                    }}
                  />
                </th>
                <th onClick={() => handleSort('contract_number')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Number <span className="sort-icon">{renderSortIcon('contract_number')}</span></div>
                </th>
                <th onClick={() => handleSort('contract_name')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Name <span className="sort-icon">{renderSortIcon('contract_name')}</span></div>
                </th>
                <th onClick={() => handleSort('customer_name')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Customer <span className="sort-icon">{renderSortIcon('customer_name')}</span></div>
                </th>
                                <th onClick={() => handleSort('currency')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Currency <span className="sort-icon">{renderSortIcon('currency')}</span></div>
                </th>
                <th onClick={() => handleSort('start_date')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Start Date <span className="sort-icon">{renderSortIcon('start_date')}</span></div>
                </th>
                <th onClick={() => handleSort('end_date')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">End Date <span className="sort-icon">{renderSortIcon('end_date')}</span></div>
                </th>
                <th onClick={() => handleSort('next_billing_start')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Upcoming Start <span className="sort-icon">{renderSortIcon('next_billing_start')}</span></div>
                </th>
                <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Status <span className="sort-icon">{renderSortIcon('status')}</span></div>
                </th>
                <th onClick={() => handleSort('invoice_count')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Invoices <span className="sort-icon">{renderSortIcon('invoice_count')}</span></div>
                </th>
                <th onClick={() => handleSort('paying_customers_count')} style={{ cursor: 'pointer' }}>
                  <div className="th-content">Paying Customers <span className="sort-icon">{renderSortIcon('paying_customers_count')}</span></div>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {[...contracts]
                .sort((a, b) => {
                  if (orderBy !== 'invoice_count') return 0;
                  const dir = orderDir === 'asc' ? 1 : -1;
                  return (resolveInvoiceCount(a) - resolveInvoiceCount(b)) * dir;
                })
                .map((contract) => {
                const status = getContractStatus(contract);
                const isExpanded = expandedContracts.has(contract.contract_id);
                const invoices = contractInvoices[contract.contract_id] || [];
                const isLoadingInvoices = loadingInvoices[contract.contract_id];
                
                return (
                <React.Fragment key={contract.contract_id}>
                <tr className={!contract.isactive || contract.isdeleted ? 'deleted-row' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedContracts.includes(contract.contract_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedContracts(prev => [...prev, contract.contract_id]);
                        } else {
                          setSelectedContracts(prev => prev.filter(id => id !== contract.contract_id));
                        }
                      }}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {contract.contract_number}
                      {/* Invoice dropdown toggle */}
                      <button
                        className="btn btn-link btn-sm p-0"
                        onClick={() => toggleInvoiceDropdown(contract.contract_id)}
                        style={{ 
                          fontSize: '12px', 
                          color: '#666',
                          textDecoration: 'none',
                          marginLeft: '5px'
                        }}
                        title={isExpanded ? 'Hide invoices' : 'Show invoices'}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <Link 
                      to={`/contracts/${contract.contract_id}`} 
                      style={{ textDecoration: 'none', color: '#3498db', fontWeight: '500' }}
                    >
                      {contract.contract_name}
                    </Link>
                  </td>
                  <td>
                    {contract.customer_id ? (
                      <Link 
                        to={`/customers/${contract.customer_id}`}
                        style={{ textDecoration: 'none', color: '#3498db' }}
                      >
                        {getCustomerName(contract.customer_id)}
                      </Link>
                    ) : (
                      <span style={{ color: '#999' }}>No Customer</span>
                    )}
                  </td>
                                    <td>
                    <span className="badge badge-info">
                      {contract.currency}
                    </span>
                  </td>
                  <td>{formatDateUTC(contract.start_date)}</td>
                  <td>{contract.end_date ? formatDateUTC(contract.end_date) : '-'}</td>
                  <td>{contract.next_billing_start ? formatDateUTC(contract.next_billing_start) : '-'}</td>
                  <td>
                    <span className={`badge ${status.className}`}>
                      {status.label}
                    </span>
                  </td>
                  <td>{
                    typeof (contract as any).invoice_count === 'number'
                      ? (contract as any).invoice_count
                      : (invoiceCounts[contract.contract_id] ?? invoices.length)
                  }</td>
                  <td>{typeof contract.paying_customers_count === 'number' ? contract.paying_customers_count : 0}</td>
                  <td>
                    {contract.isactive && !contract.isdeleted ? (
                      <>
                        <Link
                          to={`/contracts/${contract.contract_id}`}
                          className="btn btn-outline btn-sm"
                          style={{ marginRight: '5px' }}
                        >
                          View
                        </Link>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleCreateInvoice(contract)}
                          style={{ marginRight: '5px' }}
                        >
                          Create Invoice
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(contract)}
                          style={{ marginRight: '5px' }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(contract.contract_id)}
                        >
                          Delete
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleRestore(contract.contract_id)}
                      >
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
                {/* Invoice dropdown row */}
                {isExpanded && (
                  <tr>
                    <td colSpan={12} style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                      <div style={{ padding: '15px 30px' }}>
                        {isLoadingInvoices ? (
                          <div style={{ textAlign: 'center', padding: '10px' }}>
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            <span style={{ marginLeft: '10px' }}>Loading invoices...</span>
                          </div>
                        ) : invoices.length > 0 ? (
                          <div>
                            <h6 style={{ marginBottom: '10px', color: '#495057' }}>
                              Invoices ({invoices.length})
                            </h6>
                            <table className="table table-sm" style={{ marginBottom: '0' }}>
                              <thead style={{ backgroundColor: '#e9ecef' }}>
                                <tr>
                                  <th>Invoice Number</th>
                                  <th>Invoice Date</th>
                                  <th>Due Date</th>
                                  <th>Amount</th>
                                  <th>Invoice Status</th>
                                  <th>Payment Status</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoices.map((invoice) => (
                                  <tr key={invoice.invoice_id}>
                                    <td>
                                      <Link 
                                        to={`/invoices/${invoice.invoice_id}`}
                                        style={{ textDecoration: 'none', color: '#3498db' }}
                                      >
                                        {invoice.invoice_number}
                                      </Link>
                                    </td>
                                    <td>{formatDateUTC(invoice.invoice_date)}</td>
                                    <td>{formatDateUTC(invoice.due_date)}</td>
                                    <td>{formatCurrency(invoice.total_amount, invoice.currency)}</td>
                                    <td>
                                      <span className={`badge badge-${invoice.invoice_status === 'finalized' ? 'primary' : invoice.invoice_status === 'cancelled' ? 'secondary' : invoice.invoice_status === 'voided' ? 'dark' : 'warning'}`}>
                                        {invoice.invoice_status.charAt(0).toUpperCase() + invoice.invoice_status.slice(1)}
                                      </span>
                                    </td>
                                    <td>
                                      {(() => {
                                        const boletoStatus = invoice.payment_method === 'boleto' && boletoStatuses[invoice.invoice_id];
                                        const displayStatus = boletoStatus ? boletoStatus.status : invoice.payment_status;
                                        
                                        return (
                                          <span className={`badge ${getPaymentStatusBadgeClass(displayStatus)}`}>
                                            {displayStatus ? displayStatus.replace('_', ' ').charAt(0).toUpperCase() + displayStatus.replace('_', ' ').slice(1) : 'Not Due'}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td>
                                      <Link
                                        to={`/invoices/${invoice.invoice_id}`}
                                        className="btn btn-outline-primary btn-sm"
                                        style={{ marginRight: '5px' }}
                                      >
                                        View
                                      </Link>
                                      {invoice.payment_method === 'boleto' && invoice.currency === 'BRL' && (
                                        <button
                                          className="btn btn-outline-warning btn-sm"
                                          onClick={() => navigate(`/invoices/${invoice.invoice_id}`)}
                                          title="View Boleto"
                                        >
                                          Boleto
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                            <p style={{ marginBottom: '10px' }}>No invoices for this contract</p>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleCreateInvoice(contract)}
                            >
                              Create First Invoice
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              )})}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={contracts.length < pageSize}
          >
            Next
          </button>
        </div>
      </div>

      {showBulkInvoiceModal && (
        <BulkInvoiceCreationModal
          contracts={contracts.filter((c: Contract) => selectedContracts.includes(c.contract_id))}
          customers={customers}
          onClose={() => {
            setShowBulkInvoiceModal(false);
            setSelectedContracts([]);
            if (bulkInvoiceFeedback?.type === 'success' || bulkInvoiceFeedback?.type === 'warning') {
              void refreshContractsAfterBulk();
            }
          }}
          onComplete={async (createdCount, failedCount) => {
            if (createdCount > 0 && failedCount === 0) {
              setBulkInvoiceFeedback({
                type: 'success',
                text: `Created ${createdCount} draft invoice${createdCount === 1 ? '' : 's'}.`
              });
              setShowBulkInvoiceModal(false);
              setSelectedContracts([]);
              await refreshContractsAfterBulk();
              return;
            }

            if (createdCount > 0 && failedCount > 0) {
              setBulkInvoiceFeedback({
                type: 'warning',
                text: `Created ${createdCount} draft invoice${createdCount === 1 ? '' : 's'}, but ${failedCount} contract${failedCount === 1 ? '' : 's'} failed. Review failed rows in the modal and retry.`
              });
              return;
            }

            if (failedCount > 0) {
              setBulkInvoiceFeedback({
                type: 'error',
                text: `No invoices were created. ${failedCount} contract${failedCount === 1 ? '' : 's'} failed. Review errors in the modal.`
              });
            }
          }}
        />
      )}

      {showModal && (
        <Modal
          title={editingContract ? 'Edit Contract' : 'Create Contract'}
          onClose={() => setShowModal(false)}
        >
          <ContractForm
            initialData={editingContract}
            pricingHeaders={pricingHeaders}
            customers={customers}
            preselectedCustomerId={preselectedCustomerId}
            onSubmit={handleSubmit}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}
    </div>
  );
};

export default Contracts;
